import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchQuery } from "@/hooks/search/useSearchQuery";
import { Property } from "@/types/search";
import { useTenant } from "@/contexts/TenantContext";
import { ResponsiveGrid } from "@/components/shared/responsive";
import PropertyCard from "@/components/features/search/PropertyCard";
import ComplianceFooter from "@/components/features/search/ComplianceFooter";
import { EmptyStateMessage } from "@/components/common/EmptyStateMessage";
import { ArrowRight } from "lucide-react";
import { useMLSLogos } from "@/hooks/mls/useMLSLogos";
import { buildPropertyUrl } from "@/utils/propertyUrl";

interface SuggestedPropertiesProps {
  cityState: string;
}

export const SuggestedProperties: React.FC<SuggestedPropertiesProps> = ({
  cityState,
}) => {
  const navigate = useNavigate();
  const { results, loading, error, search, timestamp } = useSearchQuery();

  const { config } = useTenant();

  // Fetch MLS logos for featured properties
  const { mlsLogosMap, mlsComplianceQueries } = useMLSLogos(results);

  useEffect(() => {
    if (!config?.id || !cityState) {
      return;
    }

    const [city, state] = cityState.split(", ");
    if (!city || !state) {
      return;
    }

    // Check if any subscribedMLS entry has agentId or officeId
    const hasAgentOrOfficeId = config.subscribedMLS?.some(
      (mls) =>
        (mls.agentId && mls.agentId.length > 0) ||
        (mls.officeId && mls.officeId.length > 0)
    );

    // Only set featured: true if has agent or office ID
    const useFeature = hasAgentOrOfficeId;

    search(
      {
        tenant_id: config.id,
        location: {
          city,
          state,
        },
        filters: {
          ...(useFeature ? { featured: true } : { min_price: 350000 }),
          property_sub_type: "Single Family Residence",
          standard_status: "Active",
          rentalyn: false,
        },
      },
      10 // Request only 10 properties since we only need 6
    );
    // Runs once on mount; config/cityState are resolved before this renders.
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse">
        <ResponsiveGrid
          mobileColumns={1}
          tabletColumns={2}
          desktopColumns={3}
          gap="gap-6"
        >
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-gray-200 h-64 rounded-lg"></div>
          ))}
        </ResponsiveGrid>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (!results.length) {
    return (
      <EmptyStateMessage
        title="There are no featured listings to display"
        subtitle="Please check back later for new listings"
      />
    );
  }

  const handlePropertyClick = (property: Property) => {
    window.open(buildPropertyUrl(property, config?.id || ""), "_blank");
  };

  return (
    <div className="rounded-lg">
      <div className="flex flex-col md:flex-row items-start justify-between md:items-center mb-6 gap-3">
        <h2 className="text-lg md:text-2xl font-bold">
          Featured Properties For Sale
        </h2>
        <button
          onClick={() => {
            window.scrollTo(0, 0);
            navigate("/search");
          }}
          className="text-xs xl:text-sm text-white hover:text-primary-700 font-medium flex items-center gap-2"
        >
          See More <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      <ResponsiveGrid
        mobileColumns={1}
        tabletColumns={2}
        desktopColumns={3}
        gap="gap-6"
      >
        {results.slice(0, 6).map((property) => (
          <PropertyCard
            key={property.mlsNumber}
            property={property}
            onClick={() => handlePropertyClick(property)}
            showScore={false}
            mlsLogo={
              property.mlsBoardCode
                ? mlsLogosMap.get(property.mlsBoardCode)
                : undefined
            }
          />
        ))}
      </ResponsiveGrid>

      {/* Add Compliance Footer */}
      {results.length > 0 && (
        <ComplianceFooter
          properties={results.slice(0, 6)}
          timestamp={timestamp}
          mlsComplianceQueries={mlsComplianceQueries}
        />
      )}
    </div>
  );
};
